import React from 'react';
import './Randomizer.css';
import { getRandomizerSettingsLocalStorage } from '../../localStorage/randomizerSettingsLocalStorage';
import Flex from '../Flex/Flex';
import Text from '../Text/Text';
import DiceIcon from '../../assets/Icons/DiceIcon.svg?react';
import V2Icon from '../../v2/components/V2Icon';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { setNeedRestartRandomizer } from '../../containers/App/actions';
import { selectNeedRestartRandomizer } from '../../containers/App/selectors';
import {
  getRandomizerStateLocalStorage,
  setRandomizerStateLocalStorage,
} from '../../localStorage/randomizerStateLocalStorage';
import createRandomIntFromRange from '../../utils/createRandomIntFromRange';

type RandomizerProps = {
  variant?: 'default' | 'chip';
};

const Randomizer = ({ variant = 'default' }: RandomizerProps) => {
  const dispatch = useDispatch();
  const [randomInt, setRandomInt] = React.useState<number | undefined>(undefined);
  const [isEnabledRandomizer, setIsEnableRandomizer] = React.useState(
    getRandomizerStateLocalStorage().isActiveRandomizer,
  );
  const intervalId = React.useRef<ReturnType<typeof setInterval>>();
  const needRestartRandomizer = useSelector(selectNeedRestartRandomizer);

  const disableRandomizer = () => {
    clearInterval(intervalId.current);
    intervalId.current = undefined;
  };

  const enableRandomizer = () => {
    const randomizerSettings = getRandomizerSettingsLocalStorage();
    setRandomInt(
      createRandomIntFromRange(+randomizerSettings.minRange, +randomizerSettings.maxRange),
    );
    intervalId.current = setInterval(() => {
      setRandomInt(
        createRandomIntFromRange(+randomizerSettings.minRange, +randomizerSettings.maxRange),
      );
    }, +randomizerSettings.delay * 1000);
  };

  React.useEffect(() => {
    if (isEnabledRandomizer) {
      enableRandomizer();
    } else {
      disableRandomizer();
    }
  }, [isEnabledRandomizer, dispatch]);

  React.useEffect(() => {
    if (isEnabledRandomizer && needRestartRandomizer) {
      disableRandomizer();
      dispatch(setNeedRestartRandomizer(false));
      enableRandomizer();
    }
  }, [isEnabledRandomizer, dispatch, needRestartRandomizer]);

  const toggleRandomizer = () => {
    setIsEnableRandomizer((x) => {
      setRandomizerStateLocalStorage(!x);
      return !x;
    });
  };

  if (variant === 'chip') {
    return (
      <button
        type="button"
        className={classNames('workspace-tool workspace-tool--randomizer', {
          'workspace-tool--active': isEnabledRandomizer,
        })}
        onClick={toggleRandomizer}
        aria-pressed={isEnabledRandomizer}
        title={
          isEnabledRandomizer && randomInt != null ? `Рандомайзер: ${randomInt}` : 'Рандомайзер'
        }
      >
        <V2Icon name="dice" />
        {randomInt != null ? <span className="workspace-tool__value">{randomInt}</span> : null}
      </button>
    );
  }

  return (
    <Flex align="center">
      <Flex
        align="center"
        gap={4}
        className={classNames('Randomizer', {
          'Randomizer--isEnable': isEnabledRandomizer,
        })}
        onClick={toggleRandomizer}
      >
        <i className="Randomizer__Icon">
          <DiceIcon />
        </i>

        {randomInt && (
          <Flex flex justify="center">
            <Text size={3} type={isEnabledRandomizer ? 'default' : 'secondary'}>
              {randomInt}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default Randomizer;
